import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Navigate
} from "react-router-dom";

// Import performance monitoring and optimizations
import { performanceHub, initializeOptimizations } from './utils/optimizations';
import companyDataManager from './utils/companyDataManager';
import { initializePerformanceTools } from './utils/performanceTools';

// Import ErrorBoundary
import ErrorBoundary from './components/common/ErrorBoundary';
import { NavbarActionsProvider } from './context/NavbarActionsContext';

import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'react-redux';
import store from './redux/store';
import { fetchUserProfile, setOnboardingActive } from './redux/AuthSlice/authSlice';
import { fetchUserJobs } from './redux/JobSlice/jobSlice';
import { fetchSavedJobs } from './redux/SavedJobSlice/savedJobSlice';
import { fetchTaskTimeline } from './redux/DailyTasksSlice/dailyTaskSlice';
import { checkLinkedInConnectionStatus } from './redux/LIAuthSlice/liAuthSlice';

import OnboardingModal from './components/Modals/OnboardingModal';

// import Navbaar from './components/Navbar/Navbaar';
import Jobs from './pages/NavPages/JobPage/Jobs';
import JobDetailPage from './pages/NavPages/JobPage/JobDetailPage';
// Retired from routing (see the redirects below), kept imported-out rather than
// deleted so restoring the feature is a two-line change.
// import HiringSignal from './pages/NavPages/HiringSignalPage/HiringSignal';
// import HiringSignalDetailPage from './pages/NavPages/HiringSignalPage/HiringSignalDetailPage';
import PublicSignals from './pages/NavPages/PublicSignalsPage/PublicSignals';
import PublicSignalDetailPage from './pages/NavPages/PublicSignalsPage/PublicSignalDetailPage';
import Networking from './pages/NavPages/NetworkingPage/Networking';
import DailyTasks from './pages/NavPages/DailyTasksPage/DailyTasks';
import DailyDigestPage from './pages/NavPages/DailyDigestPage/DailyDigestPage';
import Login from './pages/AuthPages/Login';
import Signup from './pages/AuthPages/Signup';
import PrivateRoute from './components/PrivateRoutes/PrivateRoutes';
import Profile from './pages/NavPages/ProfilePages/Profile';
import CompanyList from './pages/NavPages/CompanyListPage/CompanyList';

import OAuthCallback from './components/OAuth/OAuthCallback';
import SavedJobs from './pages/SavedItemsPages/SavedJobs';
import ForgotPass from './pages/AuthPages/ForgotPass';
import TermsOfService from './pages/LegalPages/TermsOfService';
import PrivacyPolicy from './pages/LegalPages/PrivacyPolicy';
import ResumeSyncer from './components/Profile/ResumeSyncer';
import MyNetwork from './pages/NavPages/MyConnectionPages/MyNetwork';
import Inbox from './pages/NavPages/MyConnectionPages/Inbox';
import Feed from './pages/NavPages/MyConnectionPages/Feed';
import Feedback from './pages/NavPages/FeedbackPage/Feedback';
import Support from './pages/SupportPage/Support';
import PaymentSuccess from './pages/PaymentPages/PaymentSuccess';
import SubscriptionPage from './pages/PaymentPages/SubscriptionPage';
import CreditsPage from './pages/PaymentPages/CreditsPage';
import ReferEarn from './pages/ReferEarnPage/ReferEarn';
import AIAssistant from './pages/NavPages/AIAssistantPage/AIAssistant';
import AIAssistantButton from './components/AIAssistant/AIAssistantButton';
import AIAssistantSidebar from './components/AIAssistant/AIAssistantSidebar';

import { connectSocket, disconnectSocket } from './utils/socketManager';

const Layout = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  
  // Open onboarding modal from ?onboarding=true and keep it mounted across
  // route changes so the feature tour overlays don't disappear.
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const shouldOpenOnboarding = search.get('onboarding') === 'true';
    if (!shouldOpenOnboarding) return;

    setIsOnboardingModalOpen(true);
    dispatch(setOnboardingActive(true));

    search.delete('onboarding');
    const nextSearch = search.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  // const hiddenNavbarRoutes = ['/login', '/signup', '/'];
  // const hideNavbar = hiddenNavbarRoutes.includes(location.pathname);
  
  // Initialize performance optimizations with error handling
  useEffect(() => {
    let optimizationCleanup = () => {};
    let toolsCleanup = () => {};
    
    try {
      // Initialize core optimizations
      optimizationCleanup = initializeOptimizations() || (() => {});
      
      try {
        // Initialize performance tools for debugging
        toolsCleanup = initializePerformanceTools() || (() => {});
      } catch (toolsError) {
        console.error('Error initializing performance tools:', toolsError);
      }
      
      try {
        // Preload companies data to warm up the cache
        // This helps avoid the 250s+ loading time when a page needs company data
        if (companyDataManager && typeof companyDataManager.preloadCompaniesData === 'function') {
          companyDataManager.preloadCompaniesData();
        }
      } catch (preloadError) {
        console.error('Error preloading companies data:', preloadError);
      }
      
      // Generate a performance report in 30 seconds
      const reportTimer = setTimeout(() => {
        try {
          performanceHub.generateReport();
        } catch (reportError) {
          console.error('Error generating performance report:', reportError);
        }
      }, 30000);
      
      return () => {
        // Clear the report timer
        clearTimeout(reportTimer);
        
        // Clean up optimizations
        try {
          if (optimizationCleanup) optimizationCleanup();
        } catch (error) {
          console.error('Error cleaning up optimizations:', error);
        }
        
        // Clean up tools
        try {
          if (toolsCleanup) toolsCleanup();
        } catch (error) {
          console.error('Error cleaning up performance tools:', error);
        }
      };
    } catch (error) {
      console.error('Error in optimization effect:', error);
      return () => {};
    }
  }, []);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  // Cross-tab auth sync: when another tab completes a Google connect/login it
  // writes 'cc:auth-sync' to localStorage; refresh this tab's profile so state
  // like isGoogleConnected updates without a manual reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'cc:auth-sync') dispatch(fetchUserProfile());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  // After a flaky load or going offline mid-session, reconnecting should refresh core lists.
  useEffect(() => {
    const uid = user?.id || user?._id;
    if (!uid) return undefined;

    const onOnline = () => {
      dispatch(fetchUserProfile());
      dispatch(fetchUserJobs());
      dispatch(fetchSavedJobs(uid));
      dispatch(fetchTaskTimeline(uid));
    };

    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [dispatch, user?.id, user?._id]);

  useEffect(() => {
    // If there is a logged-in user, establish the connection.
    const socketUserId = user?.id || user?._id;
    if (socketUserId) {
      connectSocket(socketUserId, dispatch);
      dispatch(checkLinkedInConnectionStatus());
    }
  }, [user?.id, user?._id, dispatch]);

  return (
    <NavbarActionsProvider>
      {/* {!hideNavbar && <Navbaar />} */}
      <ResumeSyncer />
      {user && (
        <>
          <AIAssistantButton />
          <AIAssistantSidebar />
        </>
      )}

      <OnboardingModal
        isOpen={isOnboardingModalOpen}
        allowRetake
        handleCloseModal={() => { setIsOnboardingModalOpen(false); dispatch(setOnboardingActive(false)); }}
      />

      <Routes>
        {/* Navigation Bar Pages */}
        <Route path='/' element={
          <PrivateRoute>
            <Jobs />
          </PrivateRoute>
        } />
        <Route path='/jobs/:jobId' element={
          <PrivateRoute>
            <JobDetailPage />
          </PrivateRoute>
        } />
        {/*
            TheirStack hiring signals are retired from the product.

            Both routes redirect to the US board rather than 404, because links
            to them still exist in the wild — saved signal cards on the pipeline
            board, and Daily Tasks sprints activated from a signal. A redirect
            lands those on something useful; a dead route would look broken.

            `replace` keeps them out of history, so Back does not bounce the user
            straight into the redirect again.

            The components below are still imported and still work; restoring the
            feature is putting these two routes back.
        */}
        <Route path='/hiringsignal' element={<Navigate to='/hiring-signals-us?direction=hiring' replace />} />
        <Route path='/hiringsignal/:signalId' element={<Navigate to='/hiring-signals-us?direction=hiring' replace />} />
        <Route path='/hiring-signals-us' element={
          <PrivateRoute>
            <PublicSignals />
          </PrivateRoute>
        } />
        <Route path='/hiring-signals-us/:ticker' element={
          <PrivateRoute>
            <PublicSignalDetailPage />
          </PrivateRoute>
        } />
        <Route path='/companies' element={
          <PrivateRoute>
            <CompanyList />
          </PrivateRoute>
        } />
        <Route path='/networking' element={
          <PrivateRoute>
            <Networking />
          </PrivateRoute>
        } />
        <Route path='/my-network' element={
          <PrivateRoute>
            <MyNetwork />
          </PrivateRoute>
        } />
        <Route path='/inbox' element={
          <PrivateRoute>
            <Inbox />
          </PrivateRoute>
        } />
        <Route path='/feed' element={
          <PrivateRoute>
            <Feed />
          </PrivateRoute>
        } />
        <Route path='/feedback' element={
          <PrivateRoute>
            <Feedback />
          </PrivateRoute>
        } />
        <Route path='/support' element={
          <PrivateRoute>
            <Support />
          </PrivateRoute>
        } />
        <Route path='/dailytasks' element={
          <PrivateRoute>
            <DailyTasks />
          </PrivateRoute>
        } />
        <Route path='/daily-digest' element={
          <PrivateRoute>
            <DailyDigestPage />
          </PrivateRoute>
        } />
        <Route path='/ai-assistant' element={
          <PrivateRoute>
            <AIAssistant />
          </PrivateRoute>
        } />
        <Route path='/profile' element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        } />

        {/* Auth Pages */}
        <Route path='/login' element={<Login />} />
        <Route path='/signup' element={<Signup />} />
        <Route path='/forgotpass' element={<ForgotPass />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />

        {/* Legal Pages */}
        <Route path='/terms' element={<TermsOfService />} />
        <Route path='/privacy' element={<PrivacyPolicy />} />

        {/* Saved Items Pages */}
        <Route path='/savedjobs' element={
          <PrivateRoute>
            <SavedJobs />
          </PrivateRoute>
        } />

        {/* Subscription */}
        <Route path='/subscription' element={
          <PrivateRoute>
            <SubscriptionPage />
          </PrivateRoute>
        } />

        <Route path='/credits' element={
          <PrivateRoute>
            <CreditsPage />
          </PrivateRoute>
        } />

        <Route path='/refer' element={
          <PrivateRoute>
            <ReferEarn />
          </PrivateRoute>
        } />

        {/* Payment Pages */}
        <Route path='/paymentsuccess' element={
          <PrivateRoute>
            <PaymentSuccess />
          </PrivateRoute>
        } />
        <Route path='/subscriptionsuccess' element={
          <PrivateRoute>
            <PaymentSuccess />
          </PrivateRoute>
        } />
        <Route path='/payment-canceled' element={
          <PrivateRoute>
            <PaymentSuccess />
          </PrivateRoute>
        } />
        <Route path='/subscription-canceled' element={
          <PrivateRoute>
            <PaymentSuccess />
          </PrivateRoute>
        } />
      </Routes>
    </NavbarActionsProvider>
  );
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <ErrorBoundary>
        <Provider store={store}>
          <Router>
            <ErrorBoundary fallback={<div className="p-6">
              <h2 className="text-xl font-bold text-red-600 mb-3">Layout Error</h2>
              <p>There was an error loading the application layout. Please try refreshing the page.</p>
              <button
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => window.location.reload()}>
                Refresh Page
              </button>
            </div>}>
              <Layout />
            </ErrorBoundary>
            <ToastContainer position="bottom-center" autoClose={3000} hideProgressBar closeOnClick pauseOnHover={false} toastStyle={{ fontSize: '13px', fontFamily: 'DM Sans, sans-serif', borderRadius: '12px', background: '#0F172A', color: '#fff' }} />
          </Router>
        </Provider>
      </ErrorBoundary>
    </div>
  );
}

export default App;